import React from 'react';
import classNames from 'classnames';

import type { Plan } from 'js/src/client';
import { BillingCycleEnum, RoleEnum, Urls } from 'js/src/client';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import Button, { BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';

type Props = {
    plan: Plan;
    switchToPlan: (plan: Plan, with_trial: boolean) => void;
    handlePlanSwitchError: (error_info: PlanSwitchErrorInfo) => void;
    className?: string;
    text?: string;
    subscribeOverrideText?: string;
    variant?: BUTTON_VARIANTS;
};

const UpgradeButton = React.memo(function UpgradeButton({
    plan,
    switchToPlan,
    handlePlanSwitchError,
    className,
    text: overrideText,
    subscribeOverrideText,
    variant: overrideVariant,
}: Props) {
    const switchToPlanInternal = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();

            const isUser = SERVER.USER;
            let isUserAdmin = false;
            let doesCurrentPlanSupportTeamMembers = false;
            let currentTeamSize = 0;
            let currentPlanPurchasedSeats = 0;
            let canTargetPlanAcceptCurrentTeamMembers = false;

            const doesTargetPlanSupportTeamMembers = !!plan.features.enable_team_invite;
            const targetPlanDefaultTeamSeats = plan.features.default_team_seats;

            if (isUser) {
                isUserAdmin = SERVER.USER.role === RoleEnum.ADMIN;
                doesCurrentPlanSupportTeamMembers =
                    SERVER.USER.capabilities.can_invite_team_members;
                currentTeamSize = SERVER.USER.capabilities.max_team_size;
                currentPlanPurchasedSeats = SERVER.USER.capabilities.purchased_seats;
                canTargetPlanAcceptCurrentTeamMembers =
                    currentTeamSize - currentPlanPurchasedSeats <= targetPlanDefaultTeamSeats;
            }

            if (
                isUser &&
                isUserAdmin &&
                doesCurrentPlanSupportTeamMembers &&
                !doesTargetPlanSupportTeamMembers &&
                !canTargetPlanAcceptCurrentTeamMembers
            ) {
                handlePlanSwitchError({
                    targetPlan: plan,
                    feature: 'can_invite_team_member',
                });
            } else if (SERVER.USER && SERVER.USER.plan !== '' && plan.name === '') {
                // Downgrading to free plan
                window.location.href = Urls.ACCOUNT_CANCEL;
            } else {
                switchToPlan(plan, false);
                // only send the event if user is logged in and the previous price was free
                if (SERVER.USER && SERVER.USER.plan_price === 0) {
                    // track button click as adding to cart
                    analytics.track(`AddToCart - ${plan.name}`);

                    // send google event
                    try {
                        gtag('event', 'conversion', {
                            send_to: 'AW-835353444/ZStFCPawvpYBEOT2qY4D',
                            plan_name: plan.name,
                        });
                    } catch (e) {
                        // ignore
                    }

                    try {
                        gtag('event', 'conversion', {
                            send_to: 'AW-372587568/xfRLCJ6brL4CELD41LEB',
                            plan_name: plan.name,
                        });
                    } catch (e) {
                        // ignore
                    }
                }
            }
        },
        [plan, switchToPlan, handlePlanSwitchError],
    );

    const signup = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        window.location.href =
            Urls.SIGNUP +
            `?${AuthQueryParams.NEXT}=${encodeURIComponent(window.location.pathname)}`;
    }, []);

    const signedIn = SERVER.USER !== null && SERVER.USER !== undefined;
    const featured = plan.featured;

    const btnClassNames = classNames(
        'upgrade-button',
        className,
        // we use google tag manager to target the upgrade button, so we need to
        // add the following classes:
        `billing-${plan.billing_cycle.toLowerCase()}`,
        `plan-${plan.display_name.toLowerCase()}`,
    );

    if (plan.is_enterprise) {
        return (
            <Button
                text="Learn about Enterprise"
                className={btnClassNames}
                variant={
                    overrideVariant ?? (featured ? BUTTON_VARIANTS.FILLED : BUTTON_VARIANTS.OUTLINE)
                }
                href={Urls.ENTERPRISE}
            />
        );
    }

    if (signedIn) {
        // convert both the user's current subscription's price and the plan's price to monthly
        let currentPrice = SERVER.USER.plan_price;
        if (SERVER.USER.plan_billing_cycle === 'annual') {
            currentPrice = currentPrice / 12.0;
        }

        let planPrice = plan.price;
        if (plan.billing_cycle === BillingCycleEnum.ANNUAL) {
            planPrice = planPrice / 12.0;
        }

        const isCurrentPlan = SERVER.USER.plan === plan.name;
        const disabled = isCurrentPlan;

        let text: string;
        if (isCurrentPlan) {
            text = 'Current plan';
        } else if (subscribeOverrideText) {
            text = subscribeOverrideText;
        } else if (overrideText) {
            text = overrideText;
        } else if (planPrice < currentPrice) {
            text = 'Downgrade';
        } else if (SERVER.USER.plan !== '') {
            text = 'Switch plan';
        } else {
            text = 'Upgrade';
        }

        return (
            <Button
                onClick={switchToPlanInternal}
                disabled={disabled}
                text={text}
                className={btnClassNames}
                variant={
                    overrideVariant ?? (featured ? BUTTON_VARIANTS.FILLED : BUTTON_VARIANTS.OUTLINE)
                }
            />
        );
    } else {
        return (
            <Button
                onClick={signup}
                className={btnClassNames}
                variant={
                    overrideVariant ?? (featured ? BUTTON_VARIANTS.FILLED : BUTTON_VARIANTS.OUTLINE)
                }
                text="Get started"
            />
        );
    }
});

export default UpgradeButton;
